import { formatLabelDate, toHgRetsDateFormat } from '@/helpers/dateUtils';
import { formatIntegerWithThousandSeparator } from '@/helpers/numberUtils';
import type { RegionalHighlightPurchaseDurationMonths } from '@/types/regionalHighlight';

export const formatFrancsFromChfCents = (chfCents: number, locale: string): string =>
  formatIntegerWithThousandSeparator(Math.round(chfCents / 100), locale);

export const formatChfWholeFrancsFromCents = (chfCents: number, locale: string): string =>
  `CHF ${formatFrancsFromChfCents(chfCents, locale)}.–`;

/**
 * @param monthlyPriceChfCents - Monthly list price in CHF cents (excluding VAT).
 * @returns Term total in CHF cents (monthly cents × duration).
 */
export const computeTermTotalChfCents = (
  monthlyPriceChfCents: number,
  durationMonths: RegionalHighlightPurchaseDurationMonths,
): number => monthlyPriceChfCents * durationMonths;

/** Returns whether `date` is a first-of-month `YYYY-MM-01` string (shape check only). */
export const isFirstOfMonthHgRetsDate = (date: string): boolean => /^\d{4}-\d{2}-01$/.test(date);

export const toFirstOfMonthHgRets = (date: string): string => {
  const [year, month] = date.split('-');
  return `${year}-${month}-01`;
};

export const clampContractStartDate = (date: string, min: string, max: string): string => {
  const firstOfMonth = toFirstOfMonthHgRets(date);
  if (firstOfMonth < min) {
    return min;
  }
  if (firstOfMonth > max) {
    return max;
  }
  return firstOfMonth;
};

/** Earliest allowed contract start: 1st of this month if today is the 1st, else 1st of next month. */
export const getEarliestSelectableContractStartDate = (reference = new Date()): string => {
  if (reference.getDate() === 1) {
    return toHgRetsDateFormat(new Date(reference.getFullYear(), reference.getMonth(), 1));
  }
  return toHgRetsDateFormat(new Date(reference.getFullYear(), reference.getMonth() + 1, 1));
};

/** Latest allowed contract start: 1st of the month, one year after the reference month. */
export const getLatestSelectableContractStartDate = (reference = new Date()): string =>
  toHgRetsDateFormat(new Date(reference.getFullYear() + 1, reference.getMonth(), 1));

export const parseContractStartDate = (contractStartDate: string): Date => {
  const [year, month, day] = contractStartDate.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Adds calendar months while clamping the day to the target month's last day
 * (e.g. 31 Jan + 1 month → 28/29 Feb, not 2 Mar).
 */
export const addCalendarMonths = (start: Date, months: number): Date => {
  const end = new Date(start);
  const dayOfMonth = end.getDate();
  end.setDate(1);
  end.setMonth(end.getMonth() + months);
  const lastDayOfTargetMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
  end.setDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
  return end;
};

export const formatPurchaseSummaryPeriod = (
  contractStartDate: string,
  durationMonths: RegionalHighlightPurchaseDurationMonths,
  locale: string,
): string => {
  const start = parseContractStartDate(contractStartDate);
  const end = addCalendarMonths(start, durationMonths);
  return `${formatLabelDate(start.toISOString(), locale)} – ${formatLabelDate(end.toISOString(), locale)}`;
};
